import { useRef, useState } from "react";
import { TERMS_SECTIONS, TermsSection, TermsSubsection } from "../signup/termsContent";

interface Props {
  token: string;
  solutionName: string;
  attributedName: string | null;
  onAccepted: () => void;
}

export default function PreviewTermsGate({ token, solutionName, attributedName, onAccepted }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      setScrolled(true);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      const res = await fetch(`/api/preview/terms/${token}`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to record acceptance");
      onAccepted();
    } catch {
      setError("Something went wrong. Please refresh and try again.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#05050F] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <img
              src="https://cdn.prod.website-files.com/6656cc98d077ac69272f468d/666c3d6f5a0ea57d121a1846_Logo.webp"
              alt="FixFinder"
              className="h-10 mx-auto mb-5"
            />
            <h1 className="text-2xl font-bold text-white mb-1">
              {attributedName ? `Welcome, ${attributedName}` : "Welcome"}
            </h1>
            <p className="text-[#6B96D6] text-sm">
              You've been invited to preview <span className="text-[#DDEBFF] font-medium">{solutionName}</span>
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 shadow-2xl">
            <h2 className="text-[#DDEBFF] font-semibold text-base mb-1">
              Terms & Conditions and Confidentiality Agreement
            </h2>
            <p className="text-[#6B96D6] text-xs mb-4">
              Please read and scroll to the bottom to continue.
            </p>

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="h-64 overflow-y-auto border border-white/10 rounded-lg p-4 text-sm space-y-5 bg-[#0a0c19]"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {TERMS_SECTIONS.map((section: TermsSection, i: number) => (
                <div key={i}>
                  <h3 className="text-[#DDEBFF] font-semibold mb-2">{section.title}</h3>
                  {section.subsections.map((sub: TermsSubsection, j: number) => (
                    <div key={j} className="mb-3">
                      {sub.heading && (
                        <p className="text-[#0193F8] font-medium mb-1 text-xs">{sub.heading}</p>
                      )}
                      <ul className="space-y-1">
                        {sub.items.map((item: string, k: number) => (
                          <li key={k} className="text-[#9cb3d8] text-xs leading-relaxed pl-3 relative before:content-['•'] before:absolute before:left-0 before:text-[#0193F8]">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
              <div className="text-center text-[#6B96D640] text-xs pt-2">— End of Terms —</div>
            </div>

            {!scrolled && (
              <p className="text-[#6B96D6] text-xs text-center mt-3">
                Scroll to the bottom to enable acceptance
              </p>
            )}

            {error && (
              <p className="text-red-400 text-sm text-center mt-3">{error}</p>
            )}

            <button
              onClick={handleAccept}
              disabled={!scrolled || accepting}
              className="w-full mt-4 py-3 px-4 bg-[#0193F8] hover:bg-[#0173C8] disabled:bg-[#0193F8]/30 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              {accepting ? "Confirming..." : "I Agree — View Preview"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
